import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getPageForUserRow } from '../../../../utils/utils';
import ScrollLTR from '../../../ui/scrollLTR/ScrollLTR';
import useScrollToActiveRow from '../../../../hooks/useScrollToActiveRow';
import TableHeader from './components/TableHeader';
import TableRow from './components/TableRow';
import TablePagination from './components/TablePagination/TablePagination';
import classes from './ScoreboardTable.module.css';

const PAGE_SIZE = 100;

const ScoreboardTable = ({ rows = [], boardConfig = null, userRow = null }) => {

  const { texts } = useSelector((state) => state.translate);
  const userPage = useMemo(() => getPageForUserRow(rows, userRow ,PAGE_SIZE), [rows, userRow]);
  const [currentPage, setCurrentPage] = useState(userPage);

  const isUserRow = (row) => {
    if (!userRow)
      return false;
    return String(row[userRow.key]) === String(userRow.value);
  };

  const totalPages = Math.ceil(rows.length / PAGE_SIZE);
  const isPagingEnabled = rows.length > PAGE_SIZE;

  const safePage = Math.min(currentPage, Math.max(1, totalPages));
  const visibleRows = useMemo(() => {
    if (!isPagingEnabled) return rows;
    const start = (safePage - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, safePage, isPagingEnabled]);

  useEffect(() => {
    setCurrentPage(userPage);
  }, [rows, userPage]);

  const { scrollContainerRef, activeRowRef } = useScrollToActiveRow(rows, isUserRow);

  const startIndex = isPagingEnabled ? (safePage - 1) * PAGE_SIZE : 0;

  const visibleColumns = useMemo(
    () => (boardConfig?.columns || []).filter((col) => !col.excelOnly),
    [boardConfig?.columns]
  );

  return (
    <div className={classes.tableWrapper}>
      <div className={classes.gridTable}>
        <TableHeader columns={visibleColumns} texts={texts} />
        <div className={classes.scrollContainer}>
          <ScrollLTR ref={scrollContainerRef}>
            <div className={classes.tbody}>
              {visibleRows.map((row, indexInPage) => {
                const rowIndex = startIndex + indexInPage;
                return (
                  <TableRow
                    key={row.id || row.schoolId || row.classId || row.userId || rowIndex}
                    row={row}
                    rowIndex={rowIndex}
                    columns={visibleColumns}
                    isActive={isUserRow(row)}
                    ref={isUserRow(row) ? activeRowRef : null}
                  />
                );
              })}
            </div>
          </ScrollLTR>
        </div>
        {isPagingEnabled && totalPages > 1 &&
          <TablePagination texts={texts} currentPage={safePage} totalPages={totalPages} onPageChange={setCurrentPage} />
        }
      </div>
    </div>
  );
};

export default ScoreboardTable;
